import { useEffect, useState } from 'react';
import { usePageContext } from './Page/Page';
import { Input } from '../../builder';

export const TIME_INPUT: Input = {
  name: 'time',
  type: 'string',
  required: true,
  helperText: 'Time in Pacific Daylight Time (PDT)',
  regex: {
    pattern: '^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$',
    message: 'Must be in 24-hour time format hh:MM',
  },
};

const getTimeString = (time: string, dayString: string) => {
  const leadingDigit = time.split(':')?.[0]?.length === 1 ? '0' : '';
  return `${dayString} ${leadingDigit}${time}:00 GMT-0700 (Pacific Daylight Time)`;
};

export const getAddHours = (time: string) => (hoursToAdd: number) => {
  const militaryHour = parseInt(time.split(':')[0]);
  const minutes = time.split(':')[1];
  const newMilitaryHour = militaryHour + hoursToAdd;
  const newTime = newMilitaryHour > 24 ? newMilitaryHour - 24 : newMilitaryHour;
  const newMinutes = minutes === '00' ? '' : `:${minutes}`;

  return {
    time: String(newTime > 12 ? newTime - 12 : newTime) + newMinutes,
    amPM: newTime > 11 ? (newTime === 24 ? 'AM' : 'PM') : 'AM',
  };
};

const monthMap: Record<string, string> = {
  '01': 'JAN',
  '02': 'FEB',
  '03': 'MAR',
  '04': 'APR',
  '05': 'MAY',
  '06': 'JUNE',
  '07': 'JULY',
  '08': 'AUG',
  '09': 'SEPT',
  '10': 'OCT',
  '11': 'NOV',
  '12': 'DEC',
};

const getStaticTime = (time: string, dayString: string) => {
  const militaryHours = parseInt(time.split(':')?.[0]);
  const isAfternoon = militaryHours > 11 && militaryHours < 24;
  const hour =
    (isAfternoon || militaryHours === 24) && militaryHours !== 12
      ? militaryHours - 12
      : militaryHours || 12;
  const minutes = time.split(':')?.[1];
  const twelveHourTime = `${hour}:${time.split(':')?.[1]}`;
  const timeZoneAbbreviation = 'PDT';
  const amPM = isAfternoon ? 'PM' : 'AM';

  const compressedTime = minutes === '00' ? `${hour}` : `${hour}:${minutes}`;

  const [month, day, year] = dayString?.split('/') || [];

  return {
    addHours: getAddHours(time),
    amPM,
    compressedTime,
    day: `${day} ${monthMap[month]} ${year}`,
    longTime: `${twelveHourTime} ${amPM.toLowerCase()} ${timeZoneAbbreviation}`,
    timeZone: 'Pacific Daylight Time',
    timeZoneAbbreviation: 'PDT',
    hour,
    shortTime: `${twelveHourTime} ${amPM}`,
  };
};

export function useDateTime(time = '') {
  const trimmedTime = time.trim();
  const { showLocalTime, setShowLocalTime, day } = usePageContext();
  const [shouldLocalize, setShouldLocalize] = useState(false);
  const [localTime, setLocalTime] = useState(getStaticTime(trimmedTime, day));

  useEffect(() => {
    setShouldLocalize(
      Intl.DateTimeFormat().resolvedOptions().timeZone !==
        'America/Los_Angeles',
    );
  }, []);

  useEffect(() => {
    if (!showLocalTime) return setLocalTime(getStaticTime(trimmedTime, day));
    const date = new Date(getTimeString(trimmedTime, day));
    const hour =
      (date.getHours() > 12 ? date.getHours() - 12 : date.getHours()) || 12;
    const localizedTime = date.toLocaleTimeString('en-US', {
      timeZoneName: 'short',
      hour12: true,
      hour: 'numeric',
      minute: '2-digit',
    });

    const timeZoneTime = date.toLocaleTimeString('en-US', {
      timeZoneName: 'long',
    });

    const timeZone = timeZoneTime.slice(
      (timeZoneTime.indexOf(' AM') === -1
        ? timeZoneTime.indexOf(' PM')
        : timeZoneTime.indexOf(' AM')) + 4,
    );
    const shortTime = date.toLocaleTimeString('en-US', {
      hour12: true,
      hour: 'numeric',
      minute: '2-digit',
    });
    const longTime = localizedTime.replace(' AM', ' am').replace(' PM', ' pm');
    const splitLongTime = longTime.split(' ');

    const compressedTime =
      date.getMinutes() === 0 ? `${hour}` : `${hour}:${date.getMinutes()}`;

    setLocalTime({
      addHours: getAddHours(trimmedTime),
      compressedTime,
      amPM: date.getHours() > 11 ? 'PM' : 'AM',
      hour,
      day: `${date.getDate()} SEPT 2023`,
      longTime,
      shortTime,
      timeZone,
      timeZoneAbbreviation: splitLongTime?.[splitLongTime.length - 1],
    });
  }, [trimmedTime, showLocalTime, day]);
  return {
    ...localTime,
    shouldLocalize,
    showLocalTime,
    setShowLocalTime,
  };
}
